import {FC, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {Button} from '@mui/material';
import {useSnackbar} from 'notistack';

import {SubCategory, useUpdateSubCategory} from '@entities/category';
import {useGetGeoFilterOptions} from '@entities/common';
import {CardBox} from '@shared/components';
import {createEmptyTranslationDict, resolveEnglishText} from '@shared/helpers';
import {TranslationDict} from '@shared/types';
import {FormRow} from '@widgets/common';
import {FormFields} from '../CreateSubCategory/FormFields';
import {FormValues, v8nSchema} from '../CreateSubCategory/v8nSchema';

import {COLOR__WHITE} from '@/theme/colors';

interface Props {
  categoryId: string;
  subCategory: SubCategory;
  onClose: () => void;
}

export const EditSubCategoryForm: FC<Props> = ({
  categoryId,
  subCategory,
  onClose,
}) => {
  const {enqueueSnackbar} = useSnackbar();
  const {mutateAsync: updateSubCategory, isPending} = useUpdateSubCategory();
  const {data: geoOptions = []} = useGetGeoFilterOptions();

  const [nameTranslations, setNameTranslations] = useState<TranslationDict>(
    () => subCategory.nameTranslations ?? createEmptyTranslationDict(),
  );

  const formMethods = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(v8nSchema),
    defaultValues: {
      name: subCategory.name,
      geos: geoOptions.filter(opt => (subCategory.geos ?? []).includes(opt.id)),
    },
  });
  const {
    handleSubmit,
    formState: {dirtyFields},
  } = formMethods;

  const onSubmit = async (values: FormValues): Promise<void> => {
    try {
      await updateSubCategory({
        categorySlug: categoryId,
        subCategorySlug: subCategory.id,
        nameTrans: {
          ...nameTranslations,
          en: resolveEnglishText(
            nameTranslations,
            values.name,
            !!dirtyFields.name,
          ),
        },
        geos: values.geos.map(g => g.id),
      });
      enqueueSnackbar('Sub-category updated', {variant: 'success'});
      onClose();
    } catch {
      enqueueSnackbar('Failed to update sub-category', {variant: 'error'});
    }
  };

  return (
    <CardBox bgcolor={COLOR__WHITE} maxWidth={500}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormProvider {...formMethods}>
          <FormFields
            nameTranslations={nameTranslations}
            onNameTranslationsChange={setNameTranslations}
          />
        </FormProvider>

        <FormRow width={420} mt={2}>
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            sx={{flex: 1, border: '0.4px solid #CBCDD8 !important'}}
          >
            Back
          </Button>

          <Button type="submit" disabled={isPending} sx={{flex: 2}}>
            Apply changes
          </Button>
        </FormRow>
      </form>
    </CardBox>
  );
};
